<template>
  <div
    :key="uniqueKey" class="rich-text"
    :style="richRootInlineStyle">
    <div
      id="editable"
      ref="editable"
      class="rich-text-block"
      :style="richTextEditableStyle">
    </div>
    <div
      v-if="options.disableEditing || isIndicatorReadonly"
      class="editable-mask"
      @dblclick="handleDoubleClick" />
  </div>
</template>

<script>
import MediumEditor from 'medium-editor'
import ChartMixin from '@/components/charts/mixin'
import {
  getRichTextContainerStyle,
  normalizeRichTextFontSizePx,
} from '@/utils/richTextTypography'

export default {
  name: 'RichTextBlock',
  mixins: [ChartMixin],
  props: {
    config: {
      type: Object,
    },
    globalConfig: {
      type: Object,
    },
    componentType: {
      type: String,
    },
    filterParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      disableEdit: true,
      uniqueKey: 'xxx',
      options: {
        toolbar: false,
        disableEditing: true,
        anchor: {
          placeholderText: 'Paste or type a link',
          customClassOption: 'btn',
          customClassOptionText: 'Create Button',
        },
        placeholder: {
          text: '',
          hideOnClick: false,
        },
      },
      editor: null,
    }
  },
  computed: {
    /**
     * 指标只读：容器含 B/I/U/S，正文 inherit。
     * 自定义：容器仅字号/颜色/对齐/字体，B/I/U/S 全靠 execCommand 写入 HTML，避免与容器 decoration 叠加重不掉。
     */
    richTextEditableStyle () {
      return getRichTextContainerStyle({
        titleStyle: this.config?.chartSet?.titleStyle,
        omitCharacterStyles: !this.isIndicatorReadonly,
      })
    },
    richRootInlineStyle () {
      return {
        width: this.config.chartWidth,
        height: this.config.chartHeight,
      }
    },
    isActive () {
      return this.config.active
    },
    /** 已选指标时由接口驱动正文，禁止画布内直接编辑 */
    isIndicatorReadonly () {
      return this.dataSource === 'indicatorLib' && this.selectedIndicatorIdList?.length > 0
    },
    fontColor () {
      return this.config.chartSet.titleStyle.color
    },

    bold () {
      return this.config.chartSet.titleStyle.fontStyle.bold
    },
    italic () {
      return this.config.chartSet.titleStyle.fontStyle.italic
    },
    underline () {
      return this.config.chartSet.titleStyle.fontStyle.underline
    },
    strikeThrough () {
      return this.config.chartSet.titleStyle.fontStyle.strikeThrough
    },
    color () {
      return this.config.chartSet.titleStyle.fontStyle.color
    },
    fontSize () {
      return this.config.chartSet.titleStyle.fontStyle.fontSize
    },

    fontAlign () {
      return this.config.chartSet.titleStyle.align
    },
  },
  watch: {
    isIndicatorReadonly (readonly) {
      this.$nextTick(() => this.syncIndicatorVsEditor(readonly))
    },
    'config.chartViewData.chartData': {
      handler (val) {
        if (!this.$refs.editable) return
        const next = val == null ? '' : String(val)
        if (this.isIndicatorReadonly) {
          if (this.$refs.editable.innerHTML !== next) {
            this.$refs.editable.innerHTML = next
          }
          return
        }
        const cur = this.editor?.serialize?.()?.editable?.value
        if (cur !== undefined && cur === next) return
        this.$refs.editable.innerHTML = next
        if (this.editor) {
          this.editor.destroy()
          this.editor = null
        }
        this.options = { ...this.options, ...this.getEditorOptions() }
        this.editor = new MediumEditor(this.$refs.editable, this.options)
        this.bindEditorBlur()
      },
    },
    isActive (val) {
      if (!val) {
        this.config.draggable = true
      }
      this.options.disableEditing = true
      this.options = { ...this.options, ...this.getEditorOptions() }
      this.updateEditor()
      if (this.editor && !this.isIndicatorReadonly) {
        this.config.chartViewData.chartData = this.editor.serialize().editable.value
      }
    },
    fontColor: {
      handler (val) {
        this.handleChangeStyle('ForeColor', this.fontColor)
      },
    },

    fontSize: {
      handler (val) {
        this.applyFontSizePx(val)
      },
    },
    bold (val, oldVal) {
      this.handleChangeStyle('bold')
    },
    italic (val, oldVal) {
      this.handleChangeStyle('italic')
    },
    underline (val, oldVal) {
      this.handleChangeStyle('underline')
    },
    strikeThrough (val, oldVal) {
      this.handleChangeStyle('strikeThrough')
    },
    color (val, oldVal) {
      this.handleChangeStyle('foreColor', this.config.chartSet.titleStyle.color)
      this.$nextTick(() => {
        if (this.editor && !this.isIndicatorReadonly) {
          this.config.chartViewData.chartData = this.editor.serialize().editable.value
        }
      })
    },

    fontAlign: {
      handler (newValue, oldValue) {
        const map = {
          left: 'JustifyLeft',
          center: 'JustifyCenter',
          right: 'JustifyRight',
        }
        this.handleChangeStyle(map[this.fontAlign])
      },

    },
  },
  created () {
    this.uniqueKey = Math.random()
    const ts = this.config?.chartSet?.titleStyle
    if (ts && ts.fontFamily === undefined) this.$set(ts, 'fontFamily', '')
  },

  mounted () {
    this.$refs.editable.innerHTML = this.config.chartViewData.chartData || ''
    if (this.isIndicatorReadonly) {
      this.$refs.editable.setAttribute('contenteditable', 'false')
      return
    }
    this.options = { ...this.options, ...this.getEditorOptions() }
    this.editor = new MediumEditor(this.$refs.editable, this.options)
    this.bindEditorBlur()
  },
  methods: {
    /** 指标模式不挂载 MediumEditor；自定义模式挂载并可编辑 */
    syncIndicatorVsEditor (readonly) {
      if (!this.$refs.editable) return
      if (readonly) {
        if (this.editor) {
          this.editor.destroy()
          this.editor = null
        }
        this.$refs.editable.setAttribute('contenteditable', 'false')
        const next = this.config.chartViewData.chartData == null ? '' : String(this.config.chartViewData.chartData)
        this.$refs.editable.innerHTML = next
        return
      }
      this.$refs.editable.setAttribute('contenteditable', 'true')
      this.options = { ...this.options, ...this.getEditorOptions() }
      if (!this.editor) {
        this.editor = new MediumEditor(this.$refs.editable, this.options)
        this.bindEditorBlur()
      }
    },
    normalizeFontSizePx (v) {
      return normalizeRichTextFontSizePx(v)
    },
    getEditorOptions () {
      const hasContent = !!(this.config.chartViewData?.chartData)
      return {
        toolbar: false,
        // 勿在此写 disableEditing：updateEditor 会 merge 本对象，会覆盖双击后设为 false 的可编辑状态
        anchor: this.options.anchor,
        placeholder: {
          text: this.isIndicatorReadonly
            ? ''
            : (hasContent ? '' : '双击即可输入'),
          hideOnClick: false,
        },
      }
    },
    bindEditorBlur () {
      if (!this.editor) return
      const syncHtml = () => {
        if (this.isIndicatorReadonly) return
        this.config.chartViewData.chartData = this.editor.serialize().editable.value
      }
      // 保存大屏前会 onDeactivated；若仅依赖 blur，未失焦时 chartData 仍是旧值
      this.editor.subscribe('editableInput', syncHtml)
      this.editor.subscribe('editableBlur', () => {
        if (this.isIndicatorReadonly) return
        this.editor.saveSelection()
        this.editor.restoreSelection()
        this.$nextTick(syncHtml)
      })
    },
    updateEditor () {
      if (this.isIndicatorReadonly) {
        if (this.editor) {
          this.editor.destroy()
          this.editor = null
        }
        return
      }
      if (this.editor) {
        this.editor.destroy()
        this.editor = null
      }
      this.options = { ...this.options, ...this.getEditorOptions() }
      this.editor = new MediumEditor(this.$refs.editable, this.options)
      this.bindEditorBlur()
    },

    handleDoubleClick () {
      if (this.isIndicatorReadonly) return
      this.options.disableEditing = false
      this.updateEditor()
      this.editor.origElements.focus()
      this.config.draggable = false
    },
    handleChangeStyle (item, style) {
      if (this.isIndicatorReadonly) return
      if (!this.editor) return
      const selectContent = MediumEditor.selection.getSelectionHtml(this.editor.options.ownerDocument)
      const editorContent = this.editor.serialize().editable.value
      if (!selectContent && editorContent) {
        this.handleDoubleClick()
        this.editor.selectAllContents()
      }
      this.editor.options.ownerDocument.execCommand('styleWithCSS', null, true)
      this.editor.options.ownerDocument.execCommand(item, false, style)
    },
    /**
     * 富文本字号：输入的数字就是 px（不走 1-7 档位）
     * execCommand('fontSize') 只能设置 1-7，因此用“设为 7 → 替换为 span style=font-size:Npx” 的方式。
     */
    applyFontSizePx (val) {
      if (this.isIndicatorReadonly) return
      if (!this.editor) return
      const px = this.normalizeFontSizePx(val)
      if (!px) return

      const doc = this.editor.options.ownerDocument
      const selectContent = MediumEditor.selection.getSelectionHtml(doc)
      const editorContent = this.editor.serialize().editable.value
      if (!selectContent && editorContent) {
        // 没选中任何内容时，把字号应用到全文，行为与颜色等保持一致
        this.handleDoubleClick()
        this.editor.selectAllContents()
      }

      // 1) 先用浏览器内置命令生成 <font size="7">
      doc.execCommand('styleWithCSS', null, true)
      doc.execCommand('fontSize', false, '7')

      // 2) 将 “fontSize=7” 产生的节点统一落为 px
      //    不同浏览器可能生成：
      //    - <font size="7">...</font>
      //    - <span style="font-size: xxx-large;">...</span>
      const root = this.$refs.editable
      if (!root) return
      const fonts = root.querySelectorAll('font[size="7"]')
      fonts.forEach((fontEl) => {
        const span = doc.createElement('span')
        // 保留原有行内样式（如颜色、加粗等）
        if (fontEl.getAttribute('style')) {
          span.setAttribute('style', fontEl.getAttribute('style'))
        }
        span.style.fontSize = `${px}px`
        // 移动子节点
        while (fontEl.firstChild) span.appendChild(fontEl.firstChild)
        fontEl.parentNode && fontEl.parentNode.replaceChild(span, fontEl)
      })

      // 某些环境不会生成 <font>，而是写成关键字尺寸（如 xxx-large）
      const keywordSizes = new Set([
        'xxx-large',
        'xx-large',
        'x-large',
        'large',
        'larger',
        'medium',
        'small',
        'smaller',
        'x-small',
        'xx-small',
      ])
      const styled = root.querySelectorAll('[style*="font-size"]')
      styled.forEach((el) => {
        if (!el || !el.style) return
        const fs = String(el.style.fontSize || '').trim().toLowerCase()
        if (!fs) return
        // 只处理关键字尺寸，避免误伤已有 px/pt/em/rem 等
        if (keywordSizes.has(fs)) {
          el.style.fontSize = `${px}px`
        }
      })

      // 3) 同步 HTML 到 chartData，避免未失焦时丢改动
      if (!this.isIndicatorReadonly) {
        this.config.chartViewData.chartData = this.editor.serialize().editable.value
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.rich-text {
  position: relative;
  width: 100%;
  height: 100%;
  .rich-text-block{
    cursor: default;
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding: 5px;
    color: #fff;
    &:focus-visible {
      outline: none;
    }
  }
  .editable-mask{
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }

}

/* 指标正文段落：版式由容器 titleStyle 统一提供，避免与 MediumEditor 子节点默认样式冲突 */
.rich-text-block ::v-deep .rich-indicator-line {
  margin: 0;
  font-weight: inherit;
  font-style: inherit;
  text-decoration: inherit;
  color: inherit;
  font-size: inherit;
  font-family: inherit;
  line-height: inherit;
}

</style>
